import React, { useState } from "react";
import axios from "axios";
import { API, TIMEFRAMES } from "@/lib/api";
import { Play, Loader2, TrendingUp, TrendingDown, Layers } from "lucide-react";
import { toast } from "sonner";

const POPULAR = ["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "BTCUSD", "ETHUSD", "NAS100", "SPX500"];

export default function Backtest() {
  const [symbol, setSymbol] = useState("EURUSD");
  const [timeframe, setTimeframe] = useState("H1");
  const [bars, setBars] = useState(800);
  const [riskPct, setRiskPct] = useState(1.0);
  const [requireRegime, setRequireRegime] = useState(true);
  const [result, setResult] = useState(null);
  const [wf, setWf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingWf, setLoadingWf] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/backtest`, {
        params: { symbol, timeframe, bars, risk_pct: riskPct, require_regime: requireRegime }
      });
      setResult(r.data);
    } catch { toast.error("Erreur backtest"); }
    finally { setLoading(false); }
  };

  const runWf = async () => {
    setLoadingWf(true);
    try {
      const r = await axios.get(`${API}/backtest/walk-forward`, {
        params: { symbol, timeframe, bars: Math.max(bars, 1000), n_folds: 4, risk_pct: riskPct }
      });
      setWf(r.data);
    } catch { toast.error("Erreur walk-forward"); }
    finally { setLoadingWf(false); }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight" data-testid="backtest-title">Backtest & Validation</h1>
        <p className="text-white/50 text-sm mt-1">Prouve statistiquement l&apos;edge sur historique. Coûts d&apos;exécution inclus (spread + slippage + commission).</p>
      </div>

      {/* Config */}
      <div className="panel p-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="label">Actif</label>
            <select className="field" value={symbol} onChange={(e) => setSymbol(e.target.value)} data-testid="bt-symbol">
              {POPULAR.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Timeframe</label>
            <select className="field" value={timeframe} onChange={(e) => setTimeframe(e.target.value)} data-testid="bt-timeframe">
              {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Nb de bougies</label>
            <input type="number" className="field" value={bars} onChange={(e) => setBars(+e.target.value)} data-testid="bt-bars" />
          </div>
          <div>
            <label className="label">Risque / trade %</label>
            <input type="number" step="0.1" className="field" value={riskPct} onChange={(e) => setRiskPct(+e.target.value)} data-testid="bt-risk" />
          </div>
          <div className="flex items-end">
            <label className="chip cursor-pointer w-full justify-center py-2" data-testid="bt-regime-toggle">
              <input type="checkbox" checked={requireRegime} onChange={(e) => setRequireRegime(e.target.checked)} />
              Filtre régime
            </label>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className="btn btn-primary" onClick={run} disabled={loading} data-testid="run-backtest-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Lancer le backtest
          </button>
          <button className="btn" onClick={runWf} disabled={loadingWf} data-testid="run-wf-btn">
            {loadingWf ? <Loader2 size={14} className="animate-spin" /> : <Layers size={14} />} Walk-forward (4 folds)
          </button>
        </div>
      </div>

      {/* Results */}
      {result && result.ok && (
        <div className="space-y-4" data-testid="bt-results">
          {/* Regulatory disclaimer — required for any historical performance display in EU/FR */}
          <div className="panel bg-warn/5 border-warn/30 p-3 text-[11px] text-warn/90 leading-relaxed" data-testid="bt-perf-disclaimer">
            <b>⚠ Les performances passées ne préjugent pas des performances futures.</b> Les résultats affichés
            ci-dessous proviennent d&apos;une simulation sur données historiques (backtest) et ne garantissent en
            aucun cas les résultats réels d&apos;une exécution en marché live. Les backtests peuvent surestimer
            la rentabilité réelle par <b>survivorship bias</b>, <b>data snooping</b>, ou <b>overfitting</b> — voir
            les métriques <b>PBO</b> et <b>Deflated Sharpe</b> pour évaluer la robustesse statistique du résultat.
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Kpi label="Trades" value={result.n_trades} />
            <Kpi label="Winrate" value={`${result.win_rate_pct}%`} color={result.win_rate_pct >= 50 ? "text-up" : "text-down"} />
            <Kpi label="Retour total" value={`${result.return_pct}%`} color={result.return_pct >= 0 ? "text-up" : "text-down"} />
            <Kpi label="Max DD" value={`${result.max_drawdown_pct}%`} color="text-down" />
            <Kpi label="Sharpe" value={result.sharpe_ratio} color={result.sharpe_ratio >= 1 ? "text-up" : "text-warn"} big />
            <Kpi label="Profit Factor" value={result.profit_factor} color={typeof result.profit_factor === "number" && result.profit_factor >= 1.5 ? "text-up" : "text-warn"} />
            <Kpi label="Sortino" value={result.sortino_ratio} />
            <Kpi label="Calmar" value={result.calmar_ratio} />
            <Kpi label="Expectancy" value={`$${result.expectancy}`} />
            <Kpi label="Avg Win" value={`$${result.avg_win}`} color="text-up" />
            <Kpi label="Avg Loss" value={`$${result.avg_loss}`} color="text-down" />
            <Kpi label="Équité finale" value={`$${result.final_equity}`} />
          </div>

          {/* Per-regime robustness */}
          {result.per_regime?.length > 0 && (
            <div className="panel p-4" data-testid="bt-per-regime">
              <div className="panel-title mb-2">Robustesse par régime de marché</div>
              <p className="text-[11px] text-white/45 mb-3">Chaque trade est étiqueté avec le régime détecté (ADX + Hurst + percentile de volatilité) au moment de l&apos;entrée. Une stratégie robuste doit rester cohérente d&apos;un segment à l&apos;autre.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-white/40 uppercase text-[10px] tracking-widest border-b border-white/10">
                      <th className="py-2 pr-4">Segment</th>
                      <th className="py-2 pr-4">Trades</th>
                      <th className="py-2 pr-4">Winrate</th>
                      <th className="py-2 pr-4">P&L total</th>
                      <th className="py-2">Expectancy</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {result.per_regime.map((s) => (
                      <tr key={s.segment} className="border-b border-white/5 last:border-0" data-testid={`regime-row-${s.segment}`}>
                        <td className="py-2 pr-4 text-white/80">{s.segment.replace("_haute_vol", " · haute vol").replace("_basse_vol", " · basse vol")}</td>
                        <td className="py-2 pr-4">{s.n_trades}</td>
                        <td className={`py-2 pr-4 ${s.win_rate_pct >= 50 ? "text-up" : "text-down"}`}>{s.win_rate_pct}%</td>
                        <td className={`py-2 pr-4 ${s.total_pnl >= 0 ? "text-up" : "text-down"}`}>${s.total_pnl}</td>
                        <td className={s.expectancy >= 0 ? "text-up" : "text-down"}>${s.expectancy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Equity curve */}
          <div className="panel p-4">
            <div className="panel-title text-[14px] mb-3">Courbe d&apos;équité</div>
            <EquityChart data={result.equity_curve} />
          </div>

          {/* Recent trades */}
          <div className="panel overflow-hidden">
            <div className="panel-title text-[14px] p-4 pb-2">Derniers trades ({result.trades.length})</div>
            <table className="dt">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Direction</th>
                  <th>Entrée</th>
                  <th>Sortie</th>
                  <th>Motif</th>
                  <th>PnL</th>
                  <th>Confiance</th>
                </tr>
              </thead>
              <tbody>
                {result.trades.slice(-25).reverse().map((t, i) => (
                  <tr key={i}>
                    <td className="text-white/40">{result.trades.length - i}</td>
                    <td>
                      <span className={`chip ${t.direction === "ACHAT" ? "chip-up" : "chip-down"}`}>
                        {t.direction === "ACHAT" ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {t.direction}
                      </span>
                    </td>
                    <td className="font-mono">{t.entry}</td>
                    <td className="font-mono">{t.exit}</td>
                    <td><span className={`chip ${t.reason === "TP" ? "chip-up" : "chip-down"}`}>{t.reason}</span></td>
                    <td className={`font-mono ${t.pnl >= 0 ? "text-up" : "text-down"}`}>${t.pnl}</td>
                    <td className="font-mono text-white/60">{t.confidence}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Walk-forward */}
      {wf && wf.ok && (
        <div className="panel p-4 space-y-3" data-testid="wf-results">
          <div className="flex items-center justify-between">
            <div className="panel-title text-[14px]">Walk-forward — {wf.n_folds} périodes</div>
            <span className={`chip ${wf.avg_sharpe > 1 ? "chip-up" : wf.avg_sharpe > 0 ? "chip-neutral" : "chip-down"}`}>
              Sharpe moyen {wf.avg_sharpe} · Cohérence {wf.consistency_pct}%
            </span>
          </div>
          <div className="text-sm">{wf.verdict}</div>
          <table className="dt">
            <thead><tr><th>Fold</th><th>Trades</th><th>Winrate</th><th>Retour</th><th>Sharpe</th><th>Max DD</th></tr></thead>
            <tbody>
              {wf.folds.map((f) => (
                <tr key={f.fold}>
                  <td>#{f.fold}</td>
                  <td>{f.n_trades}</td>
                  <td>{f.win_rate_pct}%</td>
                  <td className={f.return_pct >= 0 ? "text-up" : "text-down"}>{f.return_pct}%</td>
                  <td className="font-mono">{f.sharpe}</td>
                  <td className="text-down font-mono">{f.max_dd_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, color = "text-white", big }) {
  return (
    <div className="panel p-3">
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`font-mono ${big ? "text-2xl" : "text-lg"} ${color} mt-1`}>{value}</div>
    </div>
  );
}

function EquityChart({ data }) {
  if (!data || data.length < 2) return <div className="text-white/40 text-sm">Aucune donnée</div>;
  const w = 1000, h = 220, pad = 20;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const rng = max - min || 1;
  const px = (i) => pad + (i / (data.length - 1)) * (w - 2 * pad);
  const py = (v) => pad + ((max - v) / rng) * (h - 2 * pad);
  const first = data[0];
  const last = data[data.length - 1];
  const up = last >= first;
  const points = data.map((v, i) => `${px(i)},${py(v)}`).join(" ");
  const area = `${pad},${h - pad} ${points} ${w - pad},${h - pad}`;
  const color = up ? "#00e676" : "#ff3b30";
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="chart-frame" width="100%">
      <polygon points={area} fill={color} opacity="0.12" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
      <text x={pad} y={h - 4} fontSize="10" fill="rgba(255,255,255,0.4)" fontFamily="IBM Plex Mono">${data[0].toFixed(0)}</text>
      <text x={w - 60} y={pad + 10} fontSize="10" fill="rgba(255,255,255,0.6)" fontFamily="IBM Plex Mono">${data[data.length - 1].toFixed(0)}</text>
    </svg>
  );
}
